import { findByUuid } from '@witmetrics/utils/arrays';
import { UNISON_PROJECT_TYPE_ID } from '@witmetrics/utils/constants';
import {
  camelToTitle,
  formatUserName,
  isBlank,
} from '@witmetrics/utils/strings';
import { isEmpty, isNullish } from '@witmetrics/utils/misc';
import { isNumber } from '@witmetrics/utils/numbers';
import { useLead } from '@witmetrics/data-store';
import type { FloatFieldValue } from '@witmetrics/unison-core';
import FormProvider from '@/providers/FormProvider';
import { useNewCase, type PendingTaskData } from '@/providers/NewCaseProvider';
import { useToggle } from '@/hooks/useToggle';
import { useString } from '@/hooks/useString';
import FormBuilderOverlay, {
  type FormBuilderOverlayProps,
} from '@/overlays/FormBuilderOverlay';
import Divider from '@/components/Divider';
import Box from '@/components/Box';
import ExpandableSearch from '@/components/inputs/ExpandableSearch';
import TextField from '@/components/inputs/TextField';
import UnderlineTextField from '@/components/inputs/UnderlineTextField';
import { AddIcon, EditIcon } from '@/icons';
import FilePreviewDialog from '@/dialogs/FilePreviewDialog';
import PendingTaskDialog from '../../PendingTaskDialog';
import StepTitle from '../StepTitle';
import FileItem from './FileItem';
import SubmissionItem from './SubmissionItem';
import TaskItem from './TaskItem';
import {
  classes,
  filterParamKeys,
  formatValueLabel,
  getSubmission,
  styles,
} from './utils';

export type ReviewStepProps = {
  onEditPatient: () => void;
};

export default function ReviewStep({ onEditPatient }: ReviewStepProps) {
  const {
    leadId,
    isPreVeneers,
    projectTypeId,
    serviceType,
    notes,
    name,
    params,
    selectedFiles,
    pendingSubmissions,
    pendingTasks,
    onUpdateNotes,
    onUpdateName,
    onResetName,
    onAddPendingSubmission,
    onUpdatePendingSubmission,
    onAddPendingTask,
    onUpdatePendingTask,
  } = useNewCase();
  const lead = useLead(leadId);
  const [activeUuid, setActiveUuid, clearActiveUuid] = useString();
  const [activeTaskUuid, setActiveTaskUuid, clearActiveTaskUuid] = useString();
  const [previewFileUuid, setPreviewFileUuid, clearPreviewFileUuid] =
    useString();
  const [settingsSearch, setSettingsSearch] = useString('');
  const [isFormBuilderOpen, toggleFormBuilder] = useToggle();
  const [isPendingTaskDialogOpen, togglePendingTaskDialog] = useToggle();

  const handleNameBlur = () => {
    if (isBlank(name)) onResetName();
  };

  const handleStartNewForm = () => {
    clearActiveUuid();
    toggleFormBuilder(true);
  };

  const handleEditSubmission = (uuid: string) => {
    setActiveUuid(uuid);
    toggleFormBuilder(true);
  };

  const handleCloseFormBuilder = () => {
    toggleFormBuilder(false);
    clearActiveUuid();
  };

  const handleSaveSubmission: FormBuilderOverlayProps['onSave'] = async (
    data
  ) => {
    if (activeUuid) onUpdatePendingSubmission(activeUuid, data);
    else onAddPendingSubmission(data);
  };

  const handleStartNewTask = () => {
    clearActiveTaskUuid();
    togglePendingTaskDialog(true);
  };

  const handleEditTask = (uuid: string) => {
    setActiveTaskUuid(uuid);
    togglePendingTaskDialog(true);
  };

  const handleClosePendingTaskDialog = () => {
    togglePendingTaskDialog(false);
    clearActiveTaskUuid();
  };

  const handleSavePendingTask = (data: PendingTaskData) => {
    if (activeTaskUuid) onUpdatePendingTask(activeTaskUuid, data);
    else onAddPendingTask(data);
  };

  const handlePreviewFile = (uuid: string) => {
    setPreviewFileUuid(uuid);
  };

  const renderValue = (key: keyof typeof params) => {
    const value = params[key];
    if (isNullish(value)) return '--';
    if (Array.isArray(value))
      return (value as string[]).map(formatValueLabel).join(', ');
    if (typeof value === 'object') return (value as FloatFieldValue).label;
    if (isNumber(value)) return String(value);
    return formatValueLabel(value as string);
  };

  const files = Object.values(selectedFiles).flat();
  const activeSubmission = getSubmission(pendingSubmissions, activeUuid);
  const activeTask = activeTaskUuid
    ? findByUuid(pendingTasks, activeTaskUuid)
    : undefined;
  const previewFile = previewFileUuid
    ? findByUuid(files, previewFileUuid)
    : undefined;
  const filteredParamKeys = filterParamKeys(
    params,
    settingsSearch
  ) as (keyof typeof params)[];

  return (
    <>
      {isFormBuilderOpen && (
        <FormProvider
          initialFormId={activeSubmission?.formId}
          initialAnswers={activeSubmission?.answers}>
          <FormBuilderOverlay
            onSave={handleSaveSubmission}
            onClose={handleCloseFormBuilder}
          />
        </FormProvider>
      )}
      {isPendingTaskDialogOpen && (
        <PendingTaskDialog
          initialData={activeTask}
          onSave={handleSavePendingTask}
          onClose={handleClosePendingTaskDialog}
        />
      )}
      {previewFile && (
        <FilePreviewDialog
          selectedFile={previewFile}
          onClose={clearPreviewFileUuid}
        />
      )}
      <div className={classes.wrapper}>
        <div className={classes.column}>
          <div>
            <StepTitle
              orientation="horizontal"
              title="Notes"
              subtitle="Include notes / treatment goals (optional)"
            />
            <TextField
              multiline
              className={classes.notes}
              placeholder="Notes..."
              minRows={3}
              maxRows={4}
              value={notes}
              onChange={onUpdateNotes}
            />
          </div>
          {!!serviceType && (
            <div className={classes.settingsWrapper}>
              <div className={classes.settingsHeader}>
                <StepTitle
                  className={classes.settingsTitle}
                  title="Custom Settings"
                />
                <ExpandableSearch
                  placeholder="Search settings..."
                  value={settingsSearch}
                  onChange={setSettingsSearch}
                  onClose={() => setSettingsSearch('')}
                />
              </div>
              {isEmpty(filteredParamKeys) ? (
                <div className={classes.emptySettings}>
                  No matching settings
                </div>
              ) : (
                <ul className={classes.list}>
                  {filteredParamKeys.map((key) => (
                    <li key={key} className={classes.listItem}>
                      <h5 className={classes.label}>{camelToTitle(key)}</h5>
                      <p className={classes.value}>{renderValue(key)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <Divider flexItem orientation="vertical" />
        <div className={classes.column}>
          <div>
            <StepTitle title="Details" />
            <UnderlineTextField
              filled
              className={classes.detailsField}
              inputStyle={styles.detailsField}
              label="Case Name"
              InputLabelProps={{ shrink: true }}
              placeholder="Enter case name..."
              value={name}
              endAdornment={<EditIcon className={classes.editIcon} />}
              onChange={onUpdateName}
              onBlur={handleNameBlur}
            />
            <Box
              sx={styles.detailsField}
              className={classes.patientName}
              onClick={onEditPatient}>
              <div>
                <h5 className={classes.patientLabel}>Patient</h5>
                <div>{formatUserName(lead)}</div>
              </div>
              <EditIcon className={classes.editIcon} />
            </Box>
          </div>
          <div>
            <StepTitle orientation="horizontal" title="Tasks" />
            {pendingTasks.map((t) => (
              <TaskItem key={t.uuid} task={t} onEdit={handleEditTask} />
            ))}
            <button className={classes.docButton} onClick={handleStartNewTask}>
              <AddIcon className={classes.addIcon} /> Add Task
            </button>
          </div>
          <div>
            <StepTitle orientation="horizontal" title="Forms" />
            {pendingSubmissions.map((s) => (
              <SubmissionItem
                key={s.uuid}
                submission={s}
                onEdit={handleEditSubmission}
              />
            ))}
            <button className={classes.docButton} onClick={handleStartNewForm}>
              <AddIcon className={classes.addIcon} /> Add Form
            </button>
          </div>
          <div>
            <StepTitle title="Files" />
            {isEmpty(files) ? (
              <div className={classes.noFiles}>No files selected</div>
            ) : (
              files.map((f) => (
                <FileItem
                  key={f.uuid}
                  selectedFile={f}
                  onPreview={handlePreviewFile}
                />
              ))
            )}
          </div>
          <div>
            {projectTypeId === UNISON_PROJECT_TYPE_ID.ALIGNERS && (
              <span>{isPreVeneers /* TODO: Should have a checkbox*/}</span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
